import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useStarAiContext } from 'star-ai'
import { FormControl, Grid, TextField } from '@material-ui/core';

import { Datasource } from 'Api';
import { validateDate } from 'Helpers';

import {
  BannerType, CheckboxElement, DatePickerElement, Dropdown,
  FormControlWrapper, PickerComponent, SwitchComponent, ComponentController, InformationMessage, NumberBox
} from 'components/common';
import {
  ConditionCode,
  DefaultConditionName,
  MasterPolicyReferenceControl,
  DefaultMasterPolicyReferenceControlName,
  SecondMasterPolicyReferenceControl,
  DefaultSecondMasterPolicyReferenceControlName,
  SurplusLine,
  DefaultSurplusLineName
} from './common';
import {
  GeneralDetailsFields, PlacingBases, ReassuredPlacingBases,
  isDualStampEntity, S4000Entity, validatePlacingBasis, DocumentTypes, setZeroTime, singlePolicyStructure, isDeclaration,
  isPolicyRenewedWithCustomLink,
  TypesOfReassured,
  OrganisationDataSources,
  CoverHolderPlacingBases,
  OrganisationTypes,
  getFormattedDate,
  SingleEntityFieldNames
} from 'components/SubmissionWizard/Helpers';
import { getMdmCompanyByOrbisId, getPolicy } from 'components/SubmissionWizard/SubmissionWizard.api';
import { AssuredPopup, ReassuredPopup, BrokerContactPopup, IndividualAssuredPopup, CompanyPopup, AssuredEclipsePopup } from 'components/SubmissionWizard/Popups';
import { renderAssuredLookupView, renderAssuredLookupHeader } from 'components/SubmissionWizard/Views';

import '../GeneralDetails.css';
import { SanctionWarning } from './common/SanctionWarning';

const SubmissionSection = ({
  fields,
  initialFormState,
  renewalState,
  initialRenewalState,
  dropdownsData,
  businessEntity,
  control,
  errors,
  forceClearDatasourceState,
  setFormState,
  setRenewalState,
  setValidationValue,
  trigger,
  setLoading,
  setBannerState,
  setBusinessEntity,
  setGeneralDetailsState,
  validationRef,
  resetValidationState,
  resetFieldValidationState,
  setLineOfBusinessState,
  setForceClearDatasourceState,
  onOrganisationsDataLoad
}) => {

  // star ai
  const ai = useStarAiContext();

  const maxAddressLength = 250;
  const maxPostCodeLength = 15;

  const [abortController, setAbortController] = useState(new AbortController());
  const newAssuredValue = useRef(null);
  const [sanctionsDialogConfig, setSanctionsDialogConfig] = useState(null);
  const [sanctionWarningDialogState, setSanctionWarningDialogState] = useState(false);

  useEffect(() => {
    setFormState(previous => ({
      ...previous,
      fields: { ...previous.fields }
    }));
  }, [setFormState]);

  useEffect(() => {
    if (fields.assured?.assured) {
      trigger('assured');
    }
  }, [fields.inceptionDate]);

  useEffect(() => {
    trigger('inceptionDate');
  }, [fields.yoa, fields.inceptionDate]);

  useEffect(() => {
    let checkIfPolicyRenewed = async () => {
      if (fields.policyId && renewalState.fields.length === 0 && renewalState.dropdownsData.length === 0) {
        setLoading(true);
        let response = await getPolicy(fields.policyId);
        setLoading(false);

        if (response.success) {
          let policy = response.data;
          let isPolicyRenewed = !!policy?.detail?.renewedToId || isPolicyRenewedWithCustomLink(policy);

          setFormState(previous => ({
            ...previous,
            fields: {
              ...previous.fields,
              isExpiringPolicyRenewed: isPolicyRenewed,
              expiringProgrammeReference: policy?.detail.programRef
            }
          }));

          setValidationValue("expiringPolicyReferenceNumber", fields.expiringPolicyReferenceNumber, { shouldValidate: true });
        } else {
          setBannerState({
            show: true,
            type: BannerType.error,
            message: response.errorMessage
          });
        }
      }
    }

    checkIfPolicyRenewed();
  }, [
    fields.policyId, fields.expiringPolicyReferenceNumber, renewalState,
    setLoading, setBannerState, setFormState, setValidationValue
  ]);

  const onFieldChange = useCallback((name, value) => {
    setFormState(previous => ({
      ...previous,
      fields: { ...previous.fields, [name]: value === "" && name === "inceptionDate" ? null : value }
    }));
  }, [setFormState]);

  const getOrbisDataPacket = (fields, property, item, updateKycDetails) => {
    if (item?.source === OrganisationDataSources.Orbis && updateKycDetails) {
      return { ...fields.kycDataPacket, [property]: { ...item } };
    }

    if (!item || !item[property] || item?.source !== OrganisationDataSources.Orbis) {
      return { ...fields.kycDataPacket, [property]: null };
    }

    return fields.kycDataPacket;
  }

  const onRenewedPolicyChecked = useCallback(checked => {
    resetFieldValidationState("policyReferenceNumber");
    resetFieldValidationState("expiringProgrammeReference");

    setFormState(previous => {
      let newDropdownsDataState = {
        ...previous.dropdownsData,
        years: [],
        eclipseEntities: []
      };

      let newFieldsState = {
        ...initialFormState.fields,
        policyEntryInformation: previous.fields.policyEntryInformation,
        documentType: previous.fields.documentType,
        renewedPolicy: checked
      };

      resetValidationState({ ...newFieldsState });
      return { ...previous, fields: { ...newFieldsState }, dropdownsData: { ...newDropdownsDataState } };
    });

    setRenewalState(previous => ({
      ...previous,
      showUserDialogWarning: false,
      fields: [],
      dropdownsData: [],
      originalRenewals: []
    }));

  }, [initialFormState, setFormState, setRenewalState, resetValidationState, resetFieldValidationState]);

  const onUnderwriterChange = useCallback(async underwriterData => {
    let underwriter = underwriterData ?
      {
        underwriter: underwriterData.underwriter,
        underwriterAbbreviation: underwriterData.underwriterAbbreviation
      } : {};
    let yoaList = underwriterData ? underwriterData.yoaList : [];
    let eclipseEntities = underwriterData ? underwriterData.entitiesList : [];

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        underwriter: underwriter ?? '',
        yoa: '',
        businessEntity: '',
        broker: null,
        brokerContact: null,
        assured: null,
        masterPolicyReferenceNumber: '',
        masterPolicyId: null,
        masterAssured: '',
        masterReassured: '',
        secondMasterAssured: '',
        secondMasterReassured: '',
        _masterAssuredOrMasterReassured: '',
        _secondMasterAssuredOrMasterReassured: ''
      },
      dropdownsData: {
        ...previous.dropdownsData,
        years: yoaList,
        eclipseEntities: eclipseEntities
      }
    }));

    setValidationValue("yoa", "");
    setValidationValue("businessEntity", "");
    setValidationValue("_masterAssuredOrMasterReassured", "");
    setForceClearDatasourceState({ "assured": true });
  }, [setFormState, setValidationValue, setForceClearDatasourceState]);

  const onYOAChange = useCallback(yoa => {
    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        yoa: yoa ?? '',
        businessEntity: '',
        broker: null,
        brokerContact: null,
        masterPolicyReferenceNumber: '',
        masterPolicyId: null,
        masterAssured: '',
        masterReassured: '',
        secondMasterAssured: '',
        secondMasterReassured: '',
        _masterAssuredOrMasterReassured: '',
        _secondMasterAssuredOrMasterReassured: ''
      }
    }));

    setValidationValue("businessEntity", "");
    setValidationValue("_masterAssuredOrMasterReassured", "");
    setForceClearDatasourceState({ "assured": true, "_masterAssuredOrMasterReassured": true, "_secondMasterAssuredOrMasterReassured": true });
  }, [setFormState, setValidationValue, setForceClearDatasourceState]);

  const onEntityChange = useCallback(event => {
    let businessEntity = dropdownsData.eclipseEntities.find(x => x.entityDisplayName === event.target?.value);

    setBusinessEntity(businessEntity);

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        businessEntity: businessEntity.entityCode,
        isDualStamp: businessEntity.isDualStampEntity,
        broker: null,
        brokerContact: null,
        masterPolicyReferenceNumber: '',
        masterPolicyId: null,
        masterAssured: '',
        masterReassured: '',
        _masterAssuredOrMasterReassured: '',
        _secondMasterAssuredOrMasterReassured: '',
        secondMasterAssured: '',
        secondMasterReassured: '',
        secondMasterPolicyReferenceNumber: '',
        secondMasterPolicyId: null,
        assured: null,
        secondPlacingBasis: businessEntity.isDualStampEntity ? previous.fields.placingBasis : null,
        secondPlacingBasisCode: businessEntity.isDualStampEntity ? previous.fields.placingBasisCode : null,
        secondReassured: businessEntity.isDualStampEntity ? previous.fields.secondReassured : null,
        kycDataPacket: null,
        hamiltonId: null,
        reassured: null,
        coverHolder: null
      }
    }));

    setValidationValue("broker", null);
    setValidationValue("brokerContact", null);
    setValidationValue("assured", null);
    setValidationValue("_masterAssuredOrMasterReassured", "");
    setValidationValue("_secondMasterAssuredOrMasterReassured", "");
    setForceClearDatasourceState({ "assured": true, "_masterAssuredOrMasterReassured": true, "_secondMasterAssuredOrMasterReassured": true, "broker": true });
    abortController.abort();
  }, [abortController, dropdownsData.eclipseEntities, setBusinessEntity, setFormState, setValidationValue, setForceClearDatasourceState]);

  const clearLineOfBusinessState = placingBasis => {
    const initialLineOfBusinessState = {
      fields: {
        policyStructure: '',
        totalNumberOfPolicyReferences: 1,
        periodBasis: '',
        periodBasisCode: '',
        obligor: null
      }
    };

    setLineOfBusinessState(previous => ({
      ...previous,
      fields: {
        ...initialLineOfBusinessState.fields,
        policyStructure: previous.fields.policyStructure === '' ? (isDeclaration(placingBasis) ? previous.fields.policyStructure : singlePolicyStructure) : (isDeclaration(placingBasis) ? '' : previous.fields.policyStructure),
      },
      policies: []
    }));
  };

  const onPlacingBasisChange = useCallback(event => {
    resetFieldValidationState("assured");
    setValidationValue("inceptionDate", null);
    resetFieldValidationState("inceptionDate");

    let placingBasisName = event.target?.value;
    let placingBasis = dropdownsData.placingBases.find(p => p.name === placingBasisName);
    let contractBasis = placingBasis?.contractBasis;
    let placingBasisCode = placingBasis?.code;
    let prevPlacingBasisCode = fields.placingBasisCode;

    if (prevPlacingBasisCode === PlacingBases.Declaration || placingBasis?.code === PlacingBases.Declaration) {
      clearLineOfBusinessState(placingBasisCode);
    }

    setGeneralDetailsState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        placingBasis: placingBasisName,
        placingBasisCode: placingBasisCode
      }
    }));

    setFormState(previous => {
      let broker = previous.fields.broker;
      let brokerContact = previous.fields.brokerContact;
      let assured = null;
      let reassured = null;

      if (placingBasisCode === PlacingBases.Declaration) {
        broker = null;
        brokerContact = null;
      }

      return {
        ...previous,
        fields: {
          ...previous.fields,
          placingBasis: placingBasisName,
          placingBasisCode: placingBasisCode,
          secondPlacingBasis: previous.fields.isDualStamp ? placingBasisName : null,
          secondPlacingBasisCode: previous.fields.isDualStamp ? placingBasisCode : null,
          secondReassured: previous.fields.isDualStamp ? reassured : null,
          broker,
          brokerContact,
          assured,
          reassured,
          decalarationType: null,
          contractBasis,
          masterPolicyReferenceNumber: '',
          masterPolicyId: null,
          masterAssured: '',
          masterReassured: '',
          _masterAssuredOrMasterReassured: '',
          _secondMasterAssuredOrMasterReassured: '',
          secondMasterAssured: '',
          secondMasterReassured: '',
          secondMasterPolicyReferenceNumber: '',
          secondMasterPolicyId: null,
          coverHolder: null,
          umr: '',
          kycDataPacket: null,
          hamiltonId: null,
          typeOfReassured: '',
          address: '',
          postCode: '',
          state: '',
          stateCode: ''
        }
      };
    });

    if (placingBasisCode === PlacingBases.Declaration) {
      setValidationValue("broker", null);
      setValidationValue("brokerContact", null);
      resetFieldValidationState("decalartionType");
      setForceClearDatasourceState({ "broker": true, "brokerContact": true });
    }

    if ([PlacingBases.Binder, PlacingBases.BinderNonProportionalRI, PlacingBases.BinderProportionalRI, PlacingBases.MasterBinder].includes(placingBasisCode)) {
      setValidationValue("coverHolder", "");
      setForceClearDatasourceState({ "coverHolder": true });
    }

    if (ReassuredPlacingBases.includes(placingBasisCode)) {
      setValidationValue("reassured", "");
      setForceClearDatasourceState({ "reassured": true });
    }

    setValidationValue("assured", null);
    setValidationValue("_masterAssuredOrMasterReassured", "");
    setValidationValue("_secondMasterAssuredOrMasterReassured", "");
    setForceClearDatasourceState({ "assured": true, "_masterAssuredOrMasterReassured": true, "_secondMasterAssuredOrMasterReassured": true });
  }, [dropdownsData, fields, setFormState, resetFieldValidationState, setValidationValue, setForceClearDatasourceState, setGeneralDetailsState]);

  const onBrokerChange = useCallback(broker => {
    setValidationValue("brokerContact", null);
    setForceClearDatasourceState({ "brokerContact": true });
    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        broker: broker ?? '',
        brokerContact: null
      }
    }));
  }, [setFormState, setValidationValue, setForceClearDatasourceState]);

  const onOperatingTerritoryChange = useCallback(value => {
    let operatingTerritory = value?.operatingTerritory;
    let territoryId = value?.id;

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        operatingTerritory,
        territoryId
      }
    }));
  }, [setFormState]);

  const onTypeOfReassuredChange = useCallback(event => {
    let typeOfReassured = event.target?.value;

    resetFieldValidationState("_masterAssuredOrMasterReassured");
    resetFieldValidationState("_secondMasterAssuredOrMasterReassured");
    setValidationValue("assured", null);
    setValidationValue("secondAssured", null);
    setValidationValue("reassured", "");
    setValidationValue("coverHolder", "");
    setValidationValue("_masterAssuredOrMasterReassured", "");
    setValidationValue("_secondMasterAssuredOrMasterReassured", "");
    setValidationValue("companyForSanctionsScreening", null);

    setForceClearDatasourceState({
      "assured": true,
      "reassured": true,
      "coverHolder": true,
      "obligor": true,
      "_masterAssuredOrMasterReassured": true,
      "_secondMasterAssuredOrMasterReassured": true,
      "countryName": true,
      "countryForSanctionsScreening": true,
      "companyForSanctionsScreening": true
    });

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        typeOfReassured,
        assured: null,
        reassured: '',
        policyId: null,
        policyReferenceNumber: '',
        secondAssured: null,
        secondReassured: '',
        secondPolicyId: null,
        secondPolicyReferenceNumber: '',
        masterPolicyReferenceNumber: '',
        masterPolicyId: null,
        masterAssured: '',
        masterReassured: '',
        _masterAssuredOrMasterReassured: '',
        _secondMasterAssuredOrMasterReassured: '',
        secondMasterAssured: '',
        secondMasterReassured: '',
        secondMasterPolicyReferenceNumber: '',
        secondMasterPolicyId: null,
        coverHolder: '',
        dateOfBirth: null,
        countryName: '',
        countryCode: '',
        countryId: null,
        countryForSanctionsScreening: '',
        countryCodeForSanctionsScreening: '',
        countryIdForSanctionsScreening: null,
        companyForSanctionsScreening: null,
        address: '',
        postCode: '',
        state: '',
        stateCode: ''
      }
    }));

    setLineOfBusinessState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        obligor: null
      },
      policies: previous.policies.map(p => ({ ...p, declarationAssured: '', declarationAssuredId: null }))
    }));

    abortController.abort();
  }, [abortController, setFormState, setValidationValue, resetFieldValidationState, setForceClearDatasourceState, setLineOfBusinessState]);

  const onRenewalTypeOfReassuredChange = useCallback(event => {
    let typeOfReassured = event.target?.value;

    setValidationValue("renewalAssured", null);
    setValidationValue("renewalReassured", null);
    setValidationValue("renewalCoverHolder", null);
    setValidationValue("renewalCountryName", null);
    setValidationValue("countryForSanctionsScreening", null);
    setValidationValue("companyForSanctionsScreening", null);

    setForceClearDatasourceState({
      "renewalAssured": true,
      "renewalReassured": true,
      "renewalCoverHolder": true,
      "renewalCountryName": true,
      "countryForSanctionsScreening": true,
      "companyForSanctionsScreening": true
    });

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        typeOfReassured,
        renewalTypeOfReassured: typeOfReassured,
        renewalAssured: null,
        renewalReassured: null,
        renewalCoverHolder: null,
        renewalDateOfBirth: null,
        renewalAddress: '',
        renewalCountryName: '',
        renewalCountryCode: '',
        renewalCountryId: null,
        renewalPostCode: '',
        countryForSanctionsScreening: '',
        companyForSanctionsScreening: null,
        state: '',
        stateCode: ''
      }
    }));

    abortController.abort();
  }, [abortController, setFormState, setValidationValue, setForceClearDatasourceState]);

  const handleSanctionCheck = useCallback((passfortSanctionsFlag, changeOrgDetails) => {
    const isSanctioned = passfortSanctionsFlag?.toLowerCase() === "true";

    if (isSanctioned) {
      setSanctionWarningDialogState(true);
    } else {
      changeOrgDetails();
    }
  }, [setSanctionWarningDialogState]);

  const checkForSanctionFlag = useCallback(async (org, changeOrgDetails) => {
    if (org?.source === OrganisationDataSources.Orbis) {
      setLoading(true);
      let response = await getMdmCompanyByOrbisId(org.orbisId);
      setLoading(false);

      if (response.success) {
        let assuredData = response.data;
        handleSanctionCheck(assuredData?.sanctionsFlag, changeOrgDetails);
      }
    }
    else if (org?.source === OrganisationDataSources.Mdm) {
      handleSanctionCheck(org.sanctionsFlag, changeOrgDetails);
    }
    else {
      changeOrgDetails();
    }
  }, [setLoading, getMdmCompanyByOrbisId, handleSanctionCheck]);

  const changeAssuredDetails = useCallback(() => {
    let assured = newAssuredValue.current;
    const selectedAssured = {
      assured: assured?.assured ?? '',
      assuredId: assured?.assuredId,
      effectiveFromDate: setZeroTime(assured?.effectiveFromDate),
      effectiveToDate: setZeroTime(assured?.effectiveToDate)
    };

    setFormState(previous => {
      var updateAssuredDetails = !(ReassuredPlacingBases.includes(previous.fields.placingBasisCode) || CoverHolderPlacingBases.includes(previous.fields.placingBasisCode));
      let kycDataPacket = getOrbisDataPacket(previous.fields, "assured", assured, updateAssuredDetails);

      let updatedState = {
        assured: assured ? selectedAssured : null,
        secondAssured: previous.fields.isDualStamp ? selectedAssured : null,
        kycDataPacket,
        hamiltonId: updateAssuredDetails ? assured?.hamiltonId : previous.fields.hamiltonId,
        address: updateAssuredDetails ? '' : previous.fields.address,
        postCode: updateAssuredDetails ? '' : previous.fields.postCode,
        state: updateAssuredDetails ? '' : previous.fields.state,
        stateCode: updateAssuredDetails ? '' : previous.fields.stateCode
      };

      if (previous.fields.typeOfReassured === TypesOfReassured.Individual) {
        updatedState.address = assured?.address;
        updatedState.postCode = assured?.postcode;
        updatedState.dateOfBirth = assured?.insuredDOB;
        updatedState.familyName = assured?.familyName;
        updatedState.givenName = assured?.givenName;
      } else {
        updatedState.dateOfBirth = null;
        updatedState.familyName = '';
        updatedState.givenName = '';
      }

      return {
        ...previous,
        fields: { ...previous.fields, ...updatedState }
      };
    });
  }, [setFormState, getOrbisDataPacket]);

  const onOrganisationChange = useCallback(async (org, changeOrgDetails, typeOfOrg) => {
    newAssuredValue.current = { ...org };
    setSanctionsDialogConfig({ changeOrgDetails, typeOfOrg });

    await checkForSanctionFlag(org, changeOrgDetails);
  }, [checkForSanctionFlag, setSanctionsDialogConfig]);

  const changeCompanyForSanctionScreeningDetails = useCallback(() => {
    const company = newAssuredValue?.current;
    const selectedCompany = {
      companyForSanctionsScreening: company?.assured ?? '',
      companyIdForSanctionsScreening: company?.assuredId,
      addressForSanctionsScreening: '',
      postCodeForSanctionsScreening: ''
    };

    setFormState(previous => {
      var updateAssuredDetails = !(ReassuredPlacingBases.includes(previous.fields.placingBasisCode) || CoverHolderPlacingBases.includes(previous.fields.placingBasisCode));
      let kycDataPacket = getOrbisDataPacket(previous.fields, "companyForSanctionsScreening", company, true);
      return {
        ...previous,
        fields: {
          ...previous.fields,
          companyForSanctionsScreening: company ? selectedCompany : null,
          kycDataPacket,
          companySanctionsHamiltonId: updateAssuredDetails ? company?.hamiltonId : previous.fields.companySanctionsHamiltonId,
          state: '',
          stateCode: ''
        }
      };
    });
  }, [setFormState]);

  const changeReassuredDetails = useCallback(() => {
    let reassured = newAssuredValue?.current?.reassured ?? '';

    setFormState(previous => {
      let kycDataPacket = getOrbisDataPacket(previous.fields, "reassured", newAssuredValue?.current, true);

      return {
        ...previous,
        fields: {
          ...previous.fields,
          reassured,
          secondReassured: previous.fields.isDualStamp ? reassured : null,
          kycDataPacket,
          hamiltonId: newAssuredValue?.current?.hamiltonId,
          address: '',
          postCode: '',
          state: '',
          stateCode: ''
        }
      };
    });
  }, [setFormState, getOrbisDataPacket]);

  const changeCoverholderDetails = useCallback(() => {
    let coverHolder = newAssuredValue?.current?.coverHolder ?? '';

    setFormState(previous => {
      let kycDataPacket = getOrbisDataPacket(previous.fields, "coverholder", newAssuredValue?.current, true);

      return {
        ...previous,
        fields: {
          ...previous.fields,
          coverHolder,
          kycDataPacket,
          hamiltonId: newAssuredValue?.current?.hamiltonId
        }
      };
    });
  }, [setFormState, getOrbisDataPacket]);

  const changeRenewalAssuredDetails = useCallback(() => {
    let assured = newAssuredValue.current;
    let renewAssured = false;
    setFormState(previous => {
      var isIndividual = previous.fields.renewalTypeOfReassured === TypesOfReassured.Individual;
      let type;
      let renewalType;
      if (fields.assuredType === OrganisationTypes.Reassured.toUpperCase()) {
        renewalType = SingleEntityFieldNames.renewalReassured;
        type = OrganisationTypes.Reassured;
      } else if (fields.assuredType === OrganisationTypes.Coverholder.toUpperCase()) {
        renewalType = SingleEntityFieldNames.renewalCoverHolder;
        type = OrganisationTypes.Coverholder;
      } else {
        renewalType = SingleEntityFieldNames.renewalAssured;
        type = OrganisationTypes.Assured;
        renewAssured = true;
      }
      let kycDataPacket = getOrbisDataPacket(previous.fields, type, assured, true);

      return {
        ...previous,
        fields: {
          ...previous.fields,
          [renewalType]: renewAssured ? assured : assured?.[type] ?? null,
          kycDataPacket,
          hamiltonId: assured?.hamiltonId,
          renewalAddress: isIndividual ? assured?.address : '',
          renewalDateOfBirth: isIndividual ? assured?.insuredDOB : previous.fields.renewalDateOfBirth,
          renewalPostCode: isIndividual ? assured?.postcode : '',
          state: '',
          stateCode: ''
        }
      };
    });
  }, [setFormState, fields]);

  const onTriaSwitch = useCallback(checked => {
    resetFieldValidationState("triaPremium");
    setValidationValue("triaPremium", null);

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        tria: checked,
        triaPremium: null
      }
    }));
  
    abortController.abort();
  }, [abortController, setFormState]);

  const onAssuredPopupSubmit = useCallback((fieldNames, submitData, isUpdateRequired, isAssuredType) => {
    let assured = {
      assured: submitData.data.assured,
      assuredId: submitData.data.assuredId
    };

    let address = submitData.data?.address;
    let postCode = submitData.data?.postCode;
    let state = submitData.data?.state;
    let stateCode = submitData.data?.stateCode;
    let assuredFieldName = fieldNames.type;

    setFormState(previous => {
      return {
        ...previous,
        fields: {
          ...previous.fields,
          [assuredFieldName]: isAssuredType ? assured : assured.assured,
          [fieldNames.addressFieldName]: isUpdateRequired ? address : previous.fields.address,
          [fieldNames.postCodeFieldName]: isUpdateRequired ? postCode : previous.fields.postCode,
          [fieldNames.stateFieldName]: isUpdateRequired ? state : previous.fields.state,
          [fieldNames.stateCodeFieldName]: isUpdateRequired ? stateCode : previous.fields.stateCode,
          hamiltonId: isUpdateRequired ? null : previous.fields.hamiltonId,
        }
      }
    });

    setValidationValue(assuredFieldName, assured, { shouldValidate: true });
  }, [setFormState, setValidationValue]);

  const onIndividualPopupSubmit = useCallback((assuredType, submitData, isAssuredType) => {
    let givenName = submitData.data.givenName ?? '';
    let familyName = submitData.data.familyName ?? '';
    let address = submitData.data?.address;
    let postCode = submitData.data?.postCode;
    let state = submitData.data?.state;
    let stateCode = submitData.data?.stateCode;
    let assured = submitData.data.assured;

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        [assuredType]: isAssuredType ? { assured } : assured,
        [SingleEntityFieldNames.renewalGivenName]: givenName,
        [SingleEntityFieldNames.renewalFamilyName]: familyName,
        [SingleEntityFieldNames.renewalAddress]: address,
        [SingleEntityFieldNames.renewalPostCode]: postCode,
        [SingleEntityFieldNames.state]: state,
        [SingleEntityFieldNames.stateCode]: stateCode,
        [SingleEntityFieldNames.hamiltonId]: null
      }
    }));

    setValidationValue(assuredType, assured, { shouldValidate: true });
  }, [setFormState, setValidationValue]);

  const onCountryChange = useCallback(value => {
    let countryName = value?.shortName ?? '';
    let countryCode = value?.iso2 ?? '';
    let countryId = value?.id ?? 0;

    resetFieldValidationState("_masterAssuredOrMasterReassured");
    resetFieldValidationState("_secondMasterAssuredOrMasterReassured");
    setValidationValue("assured", null);
    setValidationValue("secondAssured", null);
    setValidationValue("reassured", "");
    setValidationValue("coverHolder", "");
    setValidationValue("_masterAssuredOrMasterReassured", "");
    setValidationValue("_secondMasterAssuredOrMasterReassured", "");

    setForceClearDatasourceState({
      "assured": true,
      "reassured": true,
      "coverHolder": true,
      "obligor": true,
      "_masterAssuredOrMasterReassured": true,
      "_secondMasterAssuredOrMasterReassured": true
    });

    setFormState(previous => {
      return {
        ...previous,
        fields: {
          ...previous.fields,
          countryName,
          countryCode,
          countryId,
          assured: null,
          reassured: '',
          policyId: null,
          policyReferenceNumber: '',
          secondAssured: null,
          secondReassured: '',
          secondPolicyId: null,
          secondPolicyReferenceNumber: '',
          masterPolicyReferenceNumber: '',
          masterPolicyId: null,
          masterAssured: '',
          masterReassured: '',
          _masterAssuredOrMasterReassured: '',
          _secondMasterAssuredOrMasterReassured: '',
          secondMasterAssured: '',
          secondMasterReassured: '',
          secondMasterPolicyReferenceNumber: '',
          secondMasterPolicyId: null,
          coverHolder: ''
        }
      };
    });

    abortController.abort();
  }, [setFormState, abortController]);

  const onAssuredCountryChange = useCallback(value => {
    let assuredCountryName = value?.shortName ?? '';
    let assuredCountryCode = value?.iso2 ?? '';

    setFormState(previous => {
      return {
        ...previous,
        fields: {
          ...previous.fields,
          assuredCountryName,
          assuredCountryCode,
        }
      };
    });

    abortController.abort();
  }, [setFormState, abortController]);

  const onRenewalCountryChange = useCallback(value => {
    let renewalCountryName = value?.shortName ?? '';
    let renewalCountryCode = value?.iso2 ?? '';
    let renewalCountryId = value?.id ?? '';

    setValidationValue("renewalAssured", null);
    setValidationValue("renewalReassured", null);
    setValidationValue("renewalCoverHolder", null);

    setForceClearDatasourceState({
      "renewalAssured": true,
      "renewalReassured": true,
      "renewalCoverHolder": true
    });

    setFormState(previous => {
      return {
        ...previous,
        fields: {
          ...previous.fields,
          renewalCountryName,
          renewalCountryCode,
          renewalCountryId,
          renewalAssured: null,
          renewalReassured: null,
          renewalCoverHolder: null
        }
      };
    });

    abortController.abort();
  }, [setFormState, setForceClearDatasourceState, setValidationValue, abortController]);

  const onCountryForSanctionsScreeningChange = useCallback(value => {
    let countryForSanctionsScreening = value?.shortName ?? '';
    let countryCodeForSanctionsScreening = value?.iso2 ?? '';
    let countryIdForSanctionsScreening = value?.id ?? 0;

    setValidationValue("companyForSanctionsScreening", null);

    setForceClearDatasourceState({
      "companyForSanctionsScreening": true
    });

    setFormState(previous => {
      return {
        ...previous,
        fields: {
          ...previous.fields,
          countryForSanctionsScreening,
          countryCodeForSanctionsScreening,
          countryIdForSanctionsScreening,
          companyForSanctionsScreening: null
        }
      };
    });

    abortController.abort();
  }, [setFormState, abortController, setForceClearDatasourceState, setValidationValue]);

  const onExpiringPolicyOrProgrammeChange = useCallback(async policy => {
    let assuredOrg;
    let reassuredOrg;
    let coverHolderOrg;
    let assuredType = policy?.assuredType;

    setLoading(true);
    let response = await getPolicy(policy?.policyId);
    setLoading(false);

    if (response.success) {
      assuredOrg = response.data?.assuredOrganisations?.find(org => org.main)?.orgName;
      reassuredOrg = response.data?.reassured?.find(org => org.main)?.orgName;
      coverHolderOrg = response.data?.coverHolderOrganisations?.find(org => org.main)?.orgName;
      if (reassuredOrg) {
        assuredType = OrganisationTypes.Reassured.toUpperCase();
      }
      if (coverHolderOrg) {
        assuredType = OrganisationTypes.Coverholder.toUpperCase();
      }
    }

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        policyId: policy?.policyId ?? '',
        expiringPolicyReferenceNumber: policy?.policyReferenceNumber ?? '',
        expiringProgrammeReference: policy?.programmeReference ?? '',
        placingBasis: policy?.placingBasis ?? '',
        placingBasisCode: policy?.placingType ?? '',
        declarationReference: policy?.declarationReference ?? '',
        umr: policy?.umr ?? '',
        renewalTypeOfReassured: '',
        updatedAssured: null,
        renewalAssured: null,
        renewalReassured: null,
        renewalCoverHolder: null,
        dateOfBirth: null,
        expiringAssured: assuredOrg ?? policy?.assuredName ?? '',
        expiringReassured: reassuredOrg ?? '',
        expiringCoverHolder: coverHolderOrg ?? '',
        assuredType: assuredType ?? ''
      }
    }));

    setRenewalState({ ...initialRenewalState });
  }, [initialRenewalState, setFormState, setRenewalState]);

  const onDateOfBirthChange = useCallback((name, date) => {
    let dateOfBirth = validateDate(date) ? getFormattedDate(date) : null;

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        [name]: dateOfBirth ?? date,
      }
    }));
  }, [setFormState]);

  const resolveCountryName = (typeOfReassuredVal, isRenewal) => {
    let fieldName;
    if (typeOfReassuredVal === TypesOfReassured.Company) {
      fieldName = isRenewal ? 'renewalCountryOfIncorporation' : 'countryOfIncorporation';
    } else if (typeOfReassuredVal === TypesOfReassured.Individual) {
      fieldName = isRenewal ? 'renewalCountryOfResidency' : 'countryOfResidency';
    } else {
      fieldName = isRenewal ? 'renewalCountryOfCover' : 'countryOfCover';
    }

    return isRenewal ? GeneralDetailsFields.renewalCountry[fieldName] : GeneralDetailsFields.country[fieldName];
  }

  const getRequiredError = name => {
    return errors[name]?.type === 'required' &&
      <span role="alert">"{GeneralDetailsFields[name]}" is required</span>;
  }

  const getCompanyRequiredError = (name, labelName) => {
    return !!labelName && errors[name]?.type === 'required' &&
      <span role="alert">{labelName} is required</span>;
  }

  const getCountryRequiredError = (name, labelName) => {
    return !!labelName && errors[name]?.type === 'required' &&
      <span role="alert">{labelName} is required</span>;
  }

  const getAssuredRequiredError = (errorAlias, assuredType) => {
    if (errors[errorAlias]?.type === 'required') {
      return <span role="alert">"{GeneralDetailsFields[assuredType]}" is required</span>;
    }
  }

  const getPlacingBasisError = name => {
    return errors[name]?.type === 'validate' &&
      <span role="alert">Selected "{GeneralDetailsFields.documentType}" cannot be associated with selected "{GeneralDetailsFields[name]}"</span>;
  }

  const getDateValidationError = name => {
    let validDate = errors[name]?.type === 'validDate' &&
      <span role="alert">"{GeneralDetailsFields[name]}" is not valid</span>;

    return validDate;
  }

  const getPolicyIsRenewedError = name => {
    return errors[name]?.type === 'validate' &&
      <span role="alert">"{GeneralDetailsFields.expiringPolicyReference}" has been renewed</span>;
  }

  const getProgrammeIsRenewedError = name => {
    return errors[name]?.type === 'validate' &&
      <span role="alert">All policy references in "{GeneralDetailsFields.expiringProgrammeReference}" have been renewed"</span>;
  }

  const renderSanctionWarning = () => {
    return (
      <SanctionWarning
        sanctionsDialogConfig={sanctionsDialogConfig}
        setSanctionsDialogConfig={setSanctionsDialogConfig}
        setValidationValue={setValidationValue}
        setForceClearDatasourceState={setForceClearDatasourceState}
        newAssuredValue={newAssuredValue}
        sanctionWarningDialogState={sanctionWarningDialogState}
        setSanctionWarningDialogState={setSanctionWarningDialogState}
        setLoading={setLoading}
      />
    );
  }

  const renderRenewedPolicyCheckbox = () => {
    const name = "renewedPolicy";

    return (
      <Grid item>
        <FormControlWrapper
          control={
            <CheckboxElement
              edge="end"
              onChange={(_event, checked) => onRenewedPolicyChecked(checked)}
              checked={fields[name]}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
          label={GeneralDetailsFields[name]}
          labelPlacement="start"
        />
      </Grid>
    );
  }

  const renderHighPrioritySanctionsCheck = () => {
    const name = "highPrioritySanctionsCheck";
    return (
      <Grid item>
        <FormControlWrapper
          control={
            <CheckboxElement
              name={name}
              checked={fields.highPrioritySanctionsCheck}
              onChange={(e, checked) => onFieldChange(e.target?.name, checked)}
            />
          }
          label={GeneralDetailsFields.highPrioritySanctionsCheck}
          labelPlacement="start"
        />
      </Grid>
    );
  }

  const renderSendTaskToOperations = (isVisible) => {
    const name = "sendTaskToOperations";

    return isVisible && (
      <Grid item>
        <FormControlWrapper
          control={
            <CheckboxElement
              name={name}
              edge="end"
              onChange={(e, checked) => onFieldChange(e.target?.name, checked)}
              checked={fields[name]}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
          label={GeneralDetailsFields[name]}
          labelPlacement="start"
        />
      </Grid>
    );
  }

  const renderUnderwriter = () => {
    return (
      <Grid item>
        <ComponentController
          name="underwriter"
          control={control}
          required
          render={({ field: { name, onBlur, onChange } }) =>
            <PickerComponent
              label={GeneralDetailsFields[name]}
              fieldName={name}
              value={fields[name]}
              datasource={Datasource.underwritersData}
              requestData={{
                BusinessEntities: dropdownsData.entitiesForFilter
              }}
              abortController={null}
              error={!!errors[name]}
              errorText={getRequiredError(name)}
              setBannerState={setBannerState}
              onChange={value => { onUnderwriterChange(value); onChange(value); }}
              onBlur={onBlur}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    );
  }

  const renderYearOfAccount = () => {
    return (
      <Grid item>
        <ComponentController
          name="yoa"
          control={control}
          required
          render={({ field: { name, onBlur, onChange } }) =>
            <FormControl fullWidth error={!!errors[name]}>
              <Dropdown
                id={name}
                name={name}
                label={GeneralDetailsFields[name]}
                disabled={dropdownsData.years.length === 0}
                value={fields.yoa}
                data={dropdownsData.years}
                onChange={e => { onYOAChange(e.target?.value); onChange(e); }}
                onBlur={onBlur}
                errorText={getRequiredError(name)}
                shouldSetDefaultValue={true}
                inputRef={ai?.fieldsRef?.[name]}
              />
            </FormControl>
          }
        />
      </Grid>
    );
  }

  const renderEntity = () => {
    let data = dropdownsData.eclipseEntities.filter(x => x.yoa === fields.yoa).map(x => ({ Key: x.entityCode, Value: x.entityDisplayName }));
    let selectedValue = data.find(x => x.Key === fields.businessEntity)?.Value ?? '';

    return (
      <Grid item>
        <ComponentController
          name="businessEntity"
          control={control}
          required
          render={({ field: { name, onBlur, onChange } }) =>
            <FormControl fullWidth error={!!errors[name]}>
              <Dropdown
                id={name}
                name={name}
                label={GeneralDetailsFields[name]}
                disabled={!fields.yoa || !dropdownsData.eclipseEntities || dropdownsData.eclipseEntities.length === 0}
                value={selectedValue}
                data={data.map(x => x.Value)}
                onChange={e => { onEntityChange(e); onChange(e); }}
                onBlur={onBlur}
                errorText={getRequiredError(name)}
                shouldSetDefaultValue={true}
                inputRef={ai?.fieldsRef?.[name]}
              />
            </FormControl>
          }
        />
      </Grid>
    );
  }

  const renderPlacingBasis = () => {
    return (
      <Grid item>
        <ComponentController
          name="placingBasis"
          control={control}
          required
          rules={{ validate: placingBasis => validatePlacingBasis(placingBasis, fields.documentType, dropdownsData.documentTypes, dropdownsData.placingBases) }}
          render={({ field: { name, onBlur, onChange } }) =>
            <FormControl fullWidth error={!!errors[name]}>
              <Dropdown
                id={name}
                name={name}
                label={GeneralDetailsFields[name]}
                value={fields[name]}
                data={dropdownsData.placingBases.map(p => p.name)}
                onChange={e => { onPlacingBasisChange(e); onChange(e); }}
                onBlur={onBlur}
                errorText={getRequiredError(name) || getPlacingBasisError(name)}
                inputRef={ai?.fieldsRef?.[name]}
              />
            </FormControl>
          }
        />
      </Grid>
    );
  }

  const renderBrokerCode = () => {
    const getBrokerInfo = (broker) => broker ? `${broker.brokerCode} - ${broker.brokerPseud} - ${broker.broker}` : null;

    return (
      <Grid item>
        <ComponentController
          name="broker"
          control={control}
          required
          render={({ field: { name, onBlur, onChange } }) =>
            <PickerComponent
              label={GeneralDetailsFields[name]}
              fieldName={name}
              value={getBrokerInfo(fields[name])}
              forceClearDatasource={forceClearDatasourceState}
              datasource={Datasource.brokers}
              abortController={null}
              disabled={!fields.businessEntity}
              requestData={{
                syndicate: businessEntity.entityCode
              }}
              error={!!errors[name]}
              errorText={getRequiredError(name)}
              setBannerState={setBannerState}
              onChange={value => { onBrokerChange(value); onChange(value); }}
              onBlur={onBlur}
              OptionsHeader={({ className }) => {
                return (
                  <Grid container direction="row" spacing={0} className={className}>
                    <Grid item xs={4}><div>Broker Code</div></Grid>
                    <Grid item xs={4}><div>Broker Name</div></Grid>
                    <Grid item xs={4}><div>Broker Pseud</div></Grid>
                  </Grid>
                );
              }}
              OptionView={({ option, className }) => {
                return (
                  <Grid container direction="row" spacing={0} className={className}>
                    <Grid item xs={4}><div>{option.brokerCode}</div></Grid>
                    <Grid item xs={4}><div>{option.broker}</div></Grid>
                    <Grid item xs={4}><div>{option.brokerPseud}</div></Grid>
                  </Grid>
                );
              }}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    );
  }

  const renderBrokerContact = () => {
    return (
      <Grid item>
        <ComponentController
          name="brokerContact"
          control={control}
          required
          render={({ field: { name, onBlur, onChange } }) =>
            <PickerComponent
              label={GeneralDetailsFields[name]}
              fieldName={name}
              value={fields[name]}
              datasource={Datasource.brokerContacts}
              forceClearDatasource={forceClearDatasourceState}
              abortController={null}
              disabled={!fields.broker}
              error={!!errors[name]}
              errorText={getRequiredError(name)}
              config={{ autoSearch: true }}
              requestData={{
                brokerId: `${fields.broker?.brokerId}`
              }}
              setValidationValue={setValidationValue}
              setFormState={setFormState}
              setBannerState={setBannerState}
              onChange={value => { onFieldChange(name, value); onChange(value); }}
              Popup={BrokerContactPopup}
              onBlur={onBlur}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    );
  }

  const renderOperatingTerritory = () => {
    const name = "operatingTerritory";

    return (
      <Grid item>
        <PickerComponent
          label={GeneralDetailsFields[name]}
          fieldName={name}
          value={fields[name]}
          datasource={Datasource[name]}
          abortController={null}
          setAbortController={setAbortController}
          setBannerState={setBannerState}
          onChange={value => { onOperatingTerritoryChange(value); }}
          inputRef={ai?.fieldsRef?.[name]}
        />
      </Grid>
    );
  }

  const renderDeclineImmediately = () => {
    const name = "declineImmediately";

    return (
      <Grid item>
        <FormControlWrapper
          control={
            <CheckboxElement
              name={name}
              edge="end"
              onChange={(e, checked) => onFieldChange(e.target?.name, checked)}
              checked={fields[name]}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
          label={GeneralDetailsFields[name]}
          labelPlacement="start"
        />
      </Grid>
    );
  }

  const renderTypeOfReassured = (name, handleTypeOfReassuredChange) => {
    let organisationTypes = dropdownsData.organisationTypes.find(x => x.placingBasis === fields.placingBasisCode)?.organisations ?? [];
    let render = fields.placingBasisCode !== PlacingBases.Declaration;
    let disabled = !fields.placingBasis || organisationTypes.length === 0;

    return render && (
      <Grid item>
        <ComponentController
          name={name}
          control={control}
          required
          render={({ field: { name, onBlur, onChange } }) =>
            <FormControl fullWidth error={!!errors[name]}>
              <Dropdown
                id={name}
                name={name}
                disabled={disabled}
                label={GeneralDetailsFields[name]}
                value={fields[name]}
                data={organisationTypes}
                onChange={e => { handleTypeOfReassuredChange(e); onChange(e); }}
                onBlur={onBlur}
                errorText={getRequiredError(name)}
                shouldSetDefaultValue={true}
                inputRef={ai?.fieldsRef?.[name]}
              />
            </FormControl>
          }
        />
      </Grid>
    );
  }

  const renderDateOfBirth = name => {
    return (
      <Grid item>
        <ComponentController
          name={name}
          control={control}
          rules={
            {
              validate: { validDate: value => validateDate(value) }
            }}
          render={({ field: { name, onChange, onBlur } }) =>
            <DatePickerElement
              id={name}
              name={name}
              label={GeneralDetailsFields[name]}
              fullWidth
              format="dd/MM/yyyy"
              value={fields[name]}
              onChange={e => { onDateOfBirthChange(name, e); onChange(e); }}
              onBlur={onBlur}
              error={!!errors[name]}
              helperText={getDateValidationError(name)}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    );
  }

  const renderAddress = name => {
    return (
      <Grid item>
        <ComponentController
          name={name}
          control={control}
          rules={{ maxLength: maxAddressLength }}
          render={({ field: { name, onBlur, onChange } }) =>
            <TextField
              id={name}
              name={name}
              label={GeneralDetailsFields[name]}
              fullWidth
              onChange={e => { onFieldChange(name, e.target?.value); onChange(e); }}
              onBlur={onBlur}
              value={fields[name]}
              error={!!errors[name]}
              helperText={<InformationMessage
                fieldName={GeneralDetailsFields[name]}
                fieldErrors={errors[name]}
                maxLength={maxAddressLength}
                showLengthInfo={true}
                length={fields[name]?.length}
              />}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    );
  }

  const renderPostCode = name => {
    return (
      <Grid item>
        <ComponentController
          name={name}
          control={control}
          rules={{ maxLength: maxPostCodeLength }}
          render={({ field: { name, onBlur, onChange } }) =>
            <TextField
              id={name}
              name={name}
              label={GeneralDetailsFields[name]}
              fullWidth
              onChange={e => { onFieldChange(name, e.target?.value); onChange(e); }}
              onBlur={onBlur}
              value={fields[name]}
            />
          }
        />
      </Grid>
    );
  }

  const renderCountry = (name, typeOfReassuredName, handleCountryChange, isRenewal, isAssuredCountry) => {
    let typeOfReassuredVal = fields[typeOfReassuredName];
    let labelName, fieldName;
    let requiredField = fields.placingBasisCode !== PlacingBases.Declaration;
    let isReinsurance = ReassuredPlacingBases.includes(fields.placingBasisCode);

    if (isAssuredCountry) {
      if (typeOfReassuredVal === TypesOfReassured.Company) {
        fieldName = 'assuredCountryOfIncorporation';
      } else {
        fieldName = 'countryOfCover';
      }

      labelName = GeneralDetailsFields.assuredCountry[fieldName];
      const hasAssuredOrReassured = fields.assured != null || fields.renewalAssured != null;
      const isValid = isReinsurance && hasAssuredOrReassured;
      if (isValid) {
        requiredField = isRenewal
          ? fields.renewalAssured?.assuredId == null
          : fields.assured?.assuredId == null;
      } else {
        requiredField = false;
      }
    }
    else {
      labelName = resolveCountryName(typeOfReassuredVal, isRenewal);
    }

    return requiredField && (
      <Grid item>
        <ComponentController
          name={name}
          control={control}
          required={requiredField}
          render={({ field: { name, onBlur, onChange } }) =>
            <PickerComponent
              label={labelName}
              fieldName={name}
              value={fields[name]}
              config={{ showAddOptionIfNoData: false }}
              datasource={Datasource.countries}
              forceClearDatasource={forceClearDatasourceState}
              abortController={abortController}
              error={!!errors[name]}
              errorText={getCountryRequiredError(name, labelName)}
              setBannerState={setBannerState}
              setValidationValue={setValidationValue}
              setLoading={setLoading}
              setFormState={setFormState}
              setAbortController={setAbortController}
              onChange={value => { handleCountryChange(value); onChange(value); }}
              onBlur={onBlur}
              OptionsHeader={({ className }) => {
                return (
                  <Grid container direction="row" spacing={0} className={className}>
                    <Grid item xs={9}><div>Country</div></Grid>
                    <Grid item xs={3}><div>Country Code</div></Grid>
                  </Grid>
                );
              }}
              OptionView={({ option, className }) => {
                return (
                  <Grid container direction="row" spacing={0} className={className}>
                    <Grid item xs={9}><div>{option.shortName}</div></Grid>
                    <Grid item xs={3}><div>{option.iso2}</div></Grid>
                  </Grid>
                );
              }}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    );
  }

  const renderAssuredEclipseLookup = () => {
    let label;
    let assuredType;
    let isRenewal = fields.renewedPolicy;
    let render = ReassuredPlacingBases.includes(fields.placingBasisCode);

    if (isRenewal) {
      label = "renewAssuredForReassured";
      assuredType = "renewalAssured";
    }
    else {
      label = "assuredOrMasterAssured";
      assuredType = "assured";
    }

    return render && (
      <Grid item>
        <ComponentController
          name={assuredType}
          control={control}
          required={false}
          render={({ field: { name, onBlur, onChange } }) =>
            <PickerComponent
              label={GeneralDetailsFields[label]}
              fieldName={name}
              value={fields[assuredType]?.assured || fields[assuredType]}
              datasource={Datasource.assuredNames}
              abortController={abortController}
              error={!!errors[name]}
              errorText={getAssuredRequiredError(name, label)}
              setBannerState={setBannerState}
              setValidationValue={setValidationValue}
              setLoading={setLoading}
              setFormState={setFormState}
              setAbortController={setAbortController}
              onChange={value => { onAssuredEclipseLookupChange(value, isRenewal); onChange(value); }}
              onBlur={onBlur}
              Popup={props => <AssuredEclipsePopup {...props} isRenewal={isRenewal} />}
              OptionsHeader={({ className }) => {
                return (
                  <Grid container direction="row" spacing={0} className={className}>
                    <Grid item xs={8}><div>Assured</div></Grid>
                    <Grid item xs={4}><div>Country</div></Grid>
                  </Grid>
                );
              }}
              OptionView={({ option, className }) => {
                return (
                  <Grid container direction="row" spacing={0} className={className}>
                    <Grid item xs={8}><div>{option.assured}</div></Grid>
                    <Grid item xs={4}><div>{option.country}</div></Grid>
                  </Grid>
                );
              }}
            />
          }
        />
      </Grid>
    );
  }

  const onAssuredEclipseLookupChange = useCallback((assured, isRenewal) => {
    const selectedAssured = {
      assured: assured?.assured ?? '',
      assuredId: assured?.assuredId,
      effectiveFromDate: setZeroTime(assured?.effectiveFromDate),
      effectiveToDate: setZeroTime(assured?.effectiveToDate)
    };
    let assuredType = isRenewal ? 'renewalAssured' : 'assured';

    resetFieldValidationState("assuredCountryName");
    setValidationValue("assuredCountryName", null);

    setForceClearDatasourceState({
      "assuredCountryName": true
    });

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        [assuredType]: assured ? selectedAssured : null,
        secondAssured: previous.fields.isDualStamp ? selectedAssured : null,
        assuredCountryName: '',
        assuredCountryCode: ''
      }
    }));
  }, [setFormState]);

  const renderAssured = () => {
    let assuredType = "assuredOrMasterAssured";
    let datasource = !fields.typeOfReassured ? Datasource.assuredNames : Datasource.organisations.assured;
    let render = fields.placingBasisCode !== PlacingBases.Declaration;
    let disabled = !fields.typeOfReassured || !fields.countryName;
    let requiredField = ReassuredPlacingBases.includes(fields.placingBasisCode) || CoverHolderPlacingBases.includes(fields.placingBasisCode);

    const popupConfig = {
      type: "assured",
      addressFieldName: SingleEntityFieldNames.address,
      postCodeFieldName: SingleEntityFieldNames.postCode,
      stateFieldName: SingleEntityFieldNames.state,
      stateCodeFieldName: SingleEntityFieldNames.stateCode
    };

    let popup = fields.typeOfReassured === TypesOfReassured.Individual ? IndividualAssuredPopup : AssuredPopup;
    let onPopupSubmit = fields.typeOfReassured === TypesOfReassured.Individual ? null : submitData => onAssuredPopupSubmit(popupConfig, submitData, !requiredField, true);

    return (render && !requiredField) && (
      <Grid item>
        <ComponentController
          name="assured"
          control={control}
          required={!requiredField}
          render={({ field: { name, onBlur, onChange } }) =>
            <PickerComponent
              label={GeneralDetailsFields[assuredType]}
              fieldName={name}
              value={fields[name]}
              datasource={datasource}
              config={{
                newValueAtTheEnd: true,
                rowsLimit: 100,
                pickerViewClass: "assured-picker-view"
              }}
              requestData={{
                isUpdateRequired: !requiredField,
                typeOfReassured: fields.typeOfReassured,
                countryName: fields.countryName,
                countryCode: fields.countryCode,
                entityDOB: fields.dateOfBirth ?? null,
                address: fields.address,
                postCode: fields.postCode,
                countryStatesData: dropdownsData.countryStatesData
              }}
              forceClearDatasource={forceClearDatasourceState}
              disabled={disabled}
              abortController={abortController}
              error={!!errors[name]}
              errorText={getAssuredRequiredError(name, assuredType)}
              setBannerState={setBannerState}
              setValidationValue={setValidationValue}
              setLoading={setLoading}
              setFormState={setFormState}
              setAbortController={setAbortController}
              onChange={value => { onOrganisationChange(value, changeAssuredDetails, 'assured'); onChange(value); }}
              onBlur={onBlur}
              onCustomDataLoad={onOrganisationsDataLoad}
              datasourceProperty="organisations"
              Popup={popup}
              onPopupSubmit={onPopupSubmit}
              OptionsHeader={renderAssuredLookupHeader(OrganisationTypes.Assured, fields.typeOfReassured)}
              OptionView={renderAssuredLookupView(OrganisationTypes.Assured, fields.typeOfReassured)}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    );
  }

  const renderCountryOfIncorporation = () => {
    let name = 'countryForSanctionsScreening';
    let labelName = GeneralDetailsFields.sanctionsScreeningCountry[name];
    let render = fields.renewedPolicy
      ? fields.renewalTypeOfReassured === TypesOfReassured.Other : fields.typeOfReassured === TypesOfReassured.Other;

    return render && (
      <Grid item container justifyContent='flex-end'>
        <Grid item xs={10}>
          <ComponentController
            name={name}
            control={control}
            render={({ field: { name, onBlur, onChange } }) =>
              <PickerComponent
                label={labelName}
                fieldName={name}
                value={fields.countryForSanctionsScreening}
                config={{ showAddOptionIfNoData: false }}
                datasource={Datasource.countries}
                forceClearDatasource={forceClearDatasourceState}
                abortController={abortController}
                setBannerState={setBannerState}
                setValidationValue={setValidationValue}
                setLoading={setLoading}
                setFormState={setFormState}
                setAbortController={setAbortController}
                onChange={value => { onCountryForSanctionsScreeningChange(value); onChange(value); }}
                onBlur={onBlur}
                OptionsHeader={({ className }) => {
                  return (
                    <Grid container direction="row" spacing={0} className={className}>
                      <Grid item xs={9}><div>Country</div></Grid>
                      <Grid item xs={3}><div>Country Code</div></Grid>
                    </Grid>
                  );
                }}
                OptionView={({ option, className }) => {
                  return (
                    <Grid container direction="row" spacing={0} className={className}>
                      <Grid item xs={9}><div>{option.shortName}</div></Grid>
                      <Grid item xs={3}><div>{option.iso2}</div></Grid>
                    </Grid>
                  );
                }}
              />
            }
          />
        </Grid>
      </Grid>
    );
  }

  const renderCompanyForSanctionsScreening = () => {
    let name = 'companyForSanctionsScreening';
    let labelName = GeneralDetailsFields.sanctionsScreeningCompany[name];
    let datasource = Datasource.organisations.assured;
    let render = fields.renewedPolicy
      ? fields.renewalTypeOfReassured === TypesOfReassured.Other : fields.typeOfReassured === TypesOfReassured.Other;
    let isCountryOfIncorporationSelected = fields.countryForSanctionsScreening;

    return render && (
      <Grid item container justifyContent='flex-end'>
        <Grid item xs={10}>
          <ComponentController
            name={name}
            control={control}
            required={isCountryOfIncorporationSelected}
            render={({ field: { name, onBlur, onChange } }) =>
              <PickerComponent
                label={labelName}
                fieldName={name}
                value={fields.companyForSanctionsScreening}
                datasource={datasource}
                config={{
                  newValueAtTheEnd: true,
                  rowsLimit: 100,
                  pickerViewClass: "assured-picker-view"
                }}
                requestData={{
                  typeOfReassured: TypesOfReassured.Company,
                  countryName: fields.countryForSanctionsScreening,
                  countryCode: fields.countryCodeForSanctionsScreening,
                  countryStatesData: dropdownsData.countryStatesData
                }}
                forceClearDatasource={forceClearDatasourceState}
                disabled={!isCountryOfIncorporationSelected}
                abortController={abortController}
                error={isCountryOfIncorporationSelected && !!errors[name]}
                errorText={isCountryOfIncorporationSelected && getCompanyRequiredError(name, labelName)}
                setBannerState={setBannerState}
                setValidationValue={setValidationValue}
                setLoading={setLoading}
                setFormState={setFormState}
                setAbortController={setAbortController}
                onChange={value => { onOrganisationChange(value, changeCompanyForSanctionScreeningDetails, 'companyForSanctionsScreening'); onChange(value); }}
                onBlur={onBlur}
                datasourceProperty="organisations"
                Popup={CompanyPopup}
                onCustomDataLoad={onOrganisationsDataLoad}
                OptionsHeader={renderAssuredLookupHeader(OrganisationTypes.Assured)}
                OptionView={renderAssuredLookupView(OrganisationTypes.Assured)}
              />
            }
          />
        </Grid>
      </Grid>
    );
  }

  const renderReassured = () => {
    let render = ReassuredPlacingBases.includes(fields.placingBasisCode);
    let disabled = !fields.typeOfReassured || !fields.countryName;
    let datasource = Datasource.organisations.reassured;

    return render && (
      <Grid item>
        <ComponentController
          name="reassured"
          control={control}
          required
          render={({ field: { name, onBlur, onChange } }) =>
            <PickerComponent
              label={GeneralDetailsFields[name]}
              fieldName={name}
              value={fields[name] ?? ''}
              abortController={abortController}
              datasource={datasource}
              config={{
                newValueAtTheEnd: true,
                rowsLimit: 100,
                pickerViewClass: "assured-picker-view"
              }}
              requestData={{
                typeOfReassured: fields.typeOfReassured,
                countryName: fields.countryName,
                countryCode: fields.countryCode,
                entityDOB: fields.dateOfBirth,
                address: fields.address,
                countryStatesData: dropdownsData.countryStatesData
              }}
              forceClearDatasource={forceClearDatasourceState}
              disabled={disabled}
              setAbortController={setAbortController}
              error={!!errors[name]}
              errorText={getRequiredError(name)}
              setBannerState={setBannerState}
              setValidationValue={setValidationValue}
              setLoading={setLoading}
              setFormState={setFormState}
              onChange={value => { onOrganisationChange(value, changeReassuredDetails, 'reassured'); onChange(value); }}
              onBlur={onBlur}
              onCustomDataLoad={onOrganisationsDataLoad}
              datasourceProperty="organisations"
              Popup={ReassuredPopup}
              OptionsHeader={renderAssuredLookupHeader(OrganisationTypes.Reassured, fields.typeOfReassured)}
              OptionView={renderAssuredLookupView(OrganisationTypes.Reassured, fields.typeOfReassured)}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    );
  }

  const renderCoverHolder = () => {
    let render = CoverHolderPlacingBases.includes(fields.placingBasisCode);
    let disabled = !fields.typeOfReassured || !fields.countryName;
    let datasource = Datasource.organisations.coverholder;

    return render && (
      <Grid item>
        <ComponentController
          name="coverHolder"
          control={control}
          required
          render={({ field: { name, onBlur, onChange } }) =>
            <PickerComponent
              label={GeneralDetailsFields[name]}
              fieldName={name}
              value={fields[name]}
              datasource={datasource}
              config={{
                rowsLimit: 100,
                pickerViewClass: "assured-picker-view"
              }}
              requestData={{
                typeOfReassured: fields.typeOfReassured,
                countryCode: fields.countryCode,
                entityDOB: fields.dateOfBirth,
                address: fields.address
              }}
              forceClearDatasource={forceClearDatasourceState}
              disabled={disabled}
              abortController={null}
              error={!!errors[name]}
              errorText={getRequiredError(name)}
              setBannerState={setBannerState}
              onChange={value => { onOrganisationChange(value, changeCoverholderDetails, 'coverHolder'); onChange(value); }}
              onBlur={onBlur}
              onCustomDataLoad={onOrganisationsDataLoad}
              datasourceProperty="organisations"
              OptionsHeader={renderAssuredLookupHeader(OrganisationTypes.Coverholder, fields.typeOfReassured)}
              OptionView={renderAssuredLookupView(OrganisationTypes.Coverholder, fields.typeOfReassured)}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    );
  }

  const renderTriaSwitch = () => {
    const name = "tria";
    let render = fields.documentType === DocumentTypes.FirmOrderOnly;
    
    return render && (
      <Grid item>
        <FormControlWrapper
          control={
            <SwitchComponent
              name={name}
              checked={fields[name]}
              onChange={e => onTriaSwitch(e.target?.checked)}
            />
          }
          label={GeneralDetailsFields[name]}
          labelPlacement="start"
        />
      </Grid>
    );
  }

  const renderNumberBox = (name, digits, render, isTriaPremium) => {
    let docType = fields.documentType === DocumentTypes.FirmOrderOnly;
    let shouldRender = docType && (isTriaPremium ? fields.tria : render);
    let required = isTriaPremium ? fields.tria : false;

    return shouldRender && (
      <Grid item>
        <NumberBox
          name={name}
          label={GeneralDetailsFields[name]}
          control={control}
          required={required}
          numberValue={fields[name]}
          digitsCount={digits}
          onValueChange={value => { onFieldChange(name, value); setValidationValue(name, value, { shouldValidate: true }); }}
          errors={errors}
          inputRef={ai?.fieldsRef?.[name]}
        />
      </Grid>
    );
  }
  
  const renderTypeOfModellingRisk = () => {
    const name = "typeOfModellingRisk";

    return (
      <Grid item>
        <FormControl fullWidth>
          <Dropdown
            id={name}
            name={name}
            label={GeneralDetailsFields[name]}
            value={fields[name]}
            emptyValue={true}
            data={dropdownsData.typesOfModellingRisk}
            onChange={e => onFieldChange(name, e.target?.value)}
            inputRef={ai?.fieldsRef?.[name]}
          />
        </FormControl>
      </Grid>
    );
  }

  const renderInceptionDate = () => {
    let required = !!(fields.documentType === DocumentTypes.FirmOrderOnly
      && fields.placingBasisCode && !isDeclaration(fields.placingBasisCode));

    const validateYear = date => {
      if (!date || !fields.yoa) {
        return true;
      }

      return date.getFullYear() === fields.yoa;
    }

    return (
      <Grid item>
        <ComponentController
          name="inceptionDate"
          required={required}
          control={control}
          rules={
            {
              validate: { validDate: value => validateDate(value) && validateYear(value) }
            }}
          render={({ field: { name, onChange, onBlur } }) =>
            <DatePickerElement
              id={name}
              name={name}
              label={GeneralDetailsFields[name]}
              fullWidth
              format="dd/MM/yyyy"
              value={fields[name]}
              onChange={e => { onFieldChange(name, e); onChange(e); }}
              onBlur={onBlur}
              error={!!errors[name]}
              helperText={getRequiredError(name) || getDateValidationError(name)}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    );
  }

  const renderConditionCode = () => {
    let render = fields.documentType === DocumentTypes.FirmOrderOnly;

    return render && (
      <ConditionCode
        name={DefaultConditionName}
        dropdownsData={dropdownsData}
        fields={fields}
        control={control}
        errors={errors}
        trigger={trigger}
        setFormState={setFormState}
        setBannerState={setBannerState}
        validationRef={validationRef}
        isRequired={true}
        inputRef={ai?.fieldsRef?.[DefaultConditionName]}
      />
    );
  }

  const renderPricingNotRequired = () => {
    let render = fields.documentType === DocumentTypes.FirmOrderOnly;
    const name = "pricingNotRequired";

    return render && (
      <Grid item>
        <FormControlWrapper
          control={
            <CheckboxElement
              edge="end"
              name={name}
              onChange={(e, checked) => onFieldChange(name, checked)}
              checked={fields[name]}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
          label={GeneralDetailsFields[name]}
          labelPlacement="start"
        />
      </Grid>
    );
  }

  const renderSurplusLine = () => {
    let render = fields.documentType === DocumentTypes.FirmOrderOnly;

    return render && (
      <SurplusLine
        dropdownsData={dropdownsData}
        fields={fields}
        control={control}
        errors={errors}
        trigger={trigger}
        setFormState={setFormState}
        setBannerState={setBannerState}
        validationRef={validationRef}
        inputRef={ai?.fieldsRef?.[DefaultSurplusLineName]}
      />
    );
  }

  const renderEeaRisk = () => {
    const name = "eeaRisk";

    return (
      <Grid item>
        <FormControlWrapper
          control={
            <CheckboxElement
              edge="end"
              name={name}
              onChange={(e, checked) => onFieldChange(name, checked)}
              checked={fields[name]}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
          label={GeneralDetailsFields[name]}
          labelPlacement="start"
        />
      </Grid>
    );
  }

  const onExpiringReferenceSwitch = useCallback(checked => {
    setValidationValue("expiringPolicyReferenceNumber", "");
    setValidationValue("expiringProgrammeReference", "");
    setValidationValue("expiringAssured", "");
    setValidationValue("expiringReassured", "");
    setValidationValue("expiringCoverHolder", "");
    setValidationValue("renewalAssured", null);
    setValidationValue("renewalReassured", null);
    setValidationValue("renewalCoverHolder", null);
    setValidationValue("renewalCountryName", null);

    setForceClearDatasourceState({
      "renewalAssured": true,
      "renewalReassured": true,
      "renewalCoverHolder": true,
      "renewalCountryName": true
    });

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        expiringReferenceSwitch: checked,
        expiringPolicyReferenceNumber: '',
        expiringProgrammeReference: '',
        expiringAssured: '',
        expiringReassured: '',
        expiringCoverHolder: '',
        renewalTypeOfReassured: '',
        renewalAssured: null,
        renewalReassured: null,
        renewalCoverHolder: null,
        renewalDateOfBirth: null,
        renewalAddress: '',
        renewalCountryName: '',
        renewalCountryCode: '',
        renewalCountryId: null,
        renewalPostCode: ''
      }
    }));
  }, [setFormState, setValidationValue]);

  const renderExpiringReferenceSwitch = () => {
    const name = "expiringReferenceSwitch";

    return (
      <Grid item>
        <FormControlWrapper
          control={
            <SwitchComponent
              name={name}
              checked={fields[name]}
              onChange={e => onExpiringReferenceSwitch(e.target?.checked)}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
          label={GeneralDetailsFields[name]}
          labelPlacement="start"
        />
      </Grid>
    );
  }

  const renderExpiringPolicyReference = () => {
    let render = !fields.expiringReferenceSwitch;

    return render && <>
      <Grid item>
        <ComponentController
          name="expiringPolicyReferenceNumber"
          control={control}
          required
          rules={{ validate: () => !fields.isExpiringPolicyRenewed }}
          render={({ field: { name, onBlur, onChange } }) =>
            <PickerComponent
              label={GeneralDetailsFields.expiringPolicyReference}
              fieldName="policyReferenceNumber"
              value={fields[name]}
              datasource={Datasource.expiringPolicies}
              requestData={{
                BusinessEntities: dropdownsData.entitiesForFilter
              }}
              error={!!errors[name]}
              errorText={getRequiredError(name) || getPolicyIsRenewedError(name)}
              config={{ pickerViewClass: "assured-picker-view" }}
              setAbortController={setAbortController}
              setBannerState={setBannerState}
              setLoading={setLoading}
              setFormState={setFormState}
              setValidationValue={setValidationValue}
              onChange={value => { onExpiringPolicyOrProgrammeChange(value); onChange(value); }}
              onBlur={onBlur}
              OptionsHeader={({ className }) => {
                return (
                  <Grid container direction="row" spacing={0} className={className}>
                    <Grid item xs={2}><div>Organisation Name</div></Grid>
                    <Grid item xs={1}><div>Organisation Type</div></Grid>
                    <Grid item xs={1}><div>Entity</div></Grid>
                    <Grid item xs={1}><div>Major Class</div></Grid>
                    <Grid item xs={1}><div>YOA</div></Grid>
                    <Grid item xs={1}><div>Policy Reference</div></Grid>
                    <Grid item xs={1}><div>Policy Status</div></Grid>
                    <Grid item xs={1}><div>Country</div></Grid>
                    <Grid item xs={1}><div>UMR</div></Grid>
                    <Grid item xs={1}><div>Dec Ref</div></Grid>
                    <Grid item xs={1}><div>Placing Basis</div></Grid>
                  </Grid>
                );
              }}
              OptionView={({ option, className }) => {
                return (
                  <Grid container direction="row" spacing={0} className={className}>
                    <Grid item xs={2}><div>{option.assured}</div></Grid>
                    <Grid item xs={1}><div>{option.assuredType}</div></Grid>
                    <Grid item xs={1}><div>{option.entity}</div></Grid>
                    <Grid item xs={1}><div>{option.majorClassCode}</div></Grid>
                    <Grid item xs={1}><div>{option.yearOfAccount}</div></Grid>
                    <Grid item xs={1}><div>{option.policyReferenceNumber}</div></Grid>
                    <Grid item xs={1}><div>{option.policyStatus}</div></Grid>
                    <Grid item xs={1}><div>{option.country}</div></Grid>
                    <Grid item xs={1}><div>{option.umr}</div></Grid>
                    <Grid item xs={1}><div>{option.declarationReference}</div></Grid>
                    <Grid item xs={1}><div>{option.placingBasis}</div></Grid>
                  </Grid>
                );
              }}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    </>;
  }

  const renderExpiringProgrammeReference = () => {
    let render = fields.expiringReferenceSwitch;

    return render && (
      <Grid item>
        <ComponentController
          name="expiringProgrammeReference"
          control={control}
          required
          render={({ field: { name, onBlur, onChange } }) =>
            <PickerComponent
              label={GeneralDetailsFields[name]}
              fieldName="programmeReference"
              value={fields[name]}
              datasource={Datasource.programmes}
              requestData={{
                BusinessEntities: dropdownsData.entitiesForFilter
              }}
              error={!!errors[name]}
              errorText={getRequiredError(name) || getProgrammeIsRenewedError(name)}
              setAbortController={setAbortController}
              setBannerState={setBannerState}
              onChange={value => { onExpiringPolicyOrProgrammeChange(value); onChange(value); }}
              onBlur={onBlur}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    );
  }

  const renderRenewalAssuredReassured = () => {
    let fieldName;
    let assuredType;
    let datasource;
    let renewalType;
    let isAssured = false;
    let isCoverHolder = false;
    if (fields.assuredType === OrganisationTypes.Reassured.toUpperCase()) {
      assuredType = OrganisationTypes.Reassured;
      datasource = Datasource.organisations.reassured;
      renewalType = SingleEntityFieldNames.renewalReassured;
      fieldName = "renewReassured";
    } else if (fields.assuredType === OrganisationTypes.Coverholder.toUpperCase()) {
      assuredType = OrganisationTypes.Coverholder;
      datasource = Datasource.organisations.coverholder;
      renewalType = SingleEntityFieldNames.renewalCoverHolder;
      fieldName = "renewCoverHolder";
      isCoverHolder = true;
    } else {
      assuredType = OrganisationTypes.Assured;
      datasource = Datasource.organisations.assured;
      renewalType = SingleEntityFieldNames.renewalAssured;
      fieldName = "renewAssuredMasterAssured";
      isAssured = true;
    }

    let disabled = !fields.renewalTypeOfReassured || !fields.renewalCountryName;

    let popup;
    let onPopupSubmit;

    const popupConfig = {
      type: renewalType,
      addressFieldName: SingleEntityFieldNames.renewalAddress,
      postCodeFieldName: SingleEntityFieldNames.renewalPostCode,
      stateFieldName: SingleEntityFieldNames.state,
      stateCodeFieldName: SingleEntityFieldNames.stateCode
    };

    if (fields.renewalTypeOfReassured !== TypesOfReassured.Individual) {
      popup = AssuredPopup;
      onPopupSubmit = submitData => onAssuredPopupSubmit(popupConfig, submitData, true, isAssured);
    } else {
      popup = IndividualAssuredPopup;
      onPopupSubmit = submitData => onIndividualPopupSubmit(renewalType, submitData, isAssured);
    }

    return (
      <Grid item>
        <ComponentController
          name={renewalType}
          control={control}
          required
          render={({ field: { name, onBlur, onChange } }) =>
            <PickerComponent
              label={GeneralDetailsFields[fieldName]}
              fieldName={name}
              value={fields[name]?.assured || fields[name]}
              datasource={datasource}
              config={{
                newValueAtTheEnd: true,
                rowsLimit: 100,
                pickerViewClass: "assured-picker-view"
              }}
              requestData={{
                typeOfReassured: fields.renewalTypeOfReassured,
                countryName: fields.renewalCountryName,
                countryCode: fields.renewalCountryCode,
                entityDOB: fields.renewalDateOfBirth ?? null,
                address: fields.renewalAaddress,
                postCode: fields.renewalPostCode,
                countryStatesData: dropdownsData.countryStatesData
              }}
              datasourceProperty="organisations"
              forceClearDatasource={forceClearDatasourceState}
              disabled={disabled}
              abortController={abortController}
              error={!!errors[name]}
              errorText={getAssuredRequiredError(name, fieldName)}
              setBannerState={setBannerState}
              setValidationValue={setValidationValue}
              setLoading={setLoading}
              setFormState={setFormState}
              setAbortController={setAbortController}
              onChange={value => { onOrganisationChange(value, changeRenewalAssuredDetails, renewalType); onChange(value); }}
              onBlur={onBlur}
              onCustomDataLoad={onOrganisationsDataLoad}
              Popup={!isCoverHolder ? popup : null}
              onPopupSubmit={onPopupSubmit}
              OptionsHeader={renderAssuredLookupHeader(assuredType, fields.renewalTypeOfReassured)}
              OptionView={renderAssuredLookupView(assuredType, fields.renewalTypeOfReassured)}
            />
          }
        />
      </Grid>
    );
  }

  const renderRenewalSection = () => {
    let render = fields.placingBasisCode !== PlacingBases.Declaration

    return render && <>
      {renderExpiringOrg("expiringReassured")}
      {renderExpiringOrg("expiringAssured")}
      {renderExpiringOrg("expiringCoverHolder")}
      {renderTypeOfReassured("renewalTypeOfReassured", onRenewalTypeOfReassuredChange)}
      {renderRenewalIndividualFiltersSection()}
      {renderCountry("renewalCountryName", "renewalTypeOfReassured", onRenewalCountryChange, true)}
      {renderRenewalAssuredReassured()}
      {renderCountryOfIncorporation()}
      {renderCompanyForSanctionsScreening()}
      {renderAssuredEclipseLookup()}
      {renderCountry("assuredCountryName", "renewalTypeOfReassured", onAssuredCountryChange, true, true)}
    </>;
  }

  const renderLookupForMasterAssuredAndPolicyReference = (
    lookupType = "lookupForMasterAssuredAndMasterPolicyReference"
  ) => {
    let entityFilter = isDualStampEntity(fields.businessEntity) ? [S4000Entity.DisplayValue] : [fields.businessEntity];

    return (<MasterPolicyReferenceControl
      entities={entityFilter}
      fields={fields}
      control={control}
      errors={errors}
      lookupType={lookupType}
      forceClearDatasourceState={forceClearDatasourceState}
      disabled={!fields.underwriter || !fields.yoa || !fields.businessEntity}
      setBannerState={setBannerState}
      setLoading={setLoading}
      setFormState={setFormState}
      setValidationValue={setValidationValue}
      abortController={abortController}
      setAbortController={setAbortController}
      inputRef={ai?.fieldsRef?.[DefaultMasterPolicyReferenceControlName]}
    />);
  }

  const renderMasterPolicyReferenceNumber = (name = "masterPolicyReferenceNumber") => {
    return (
      <Grid item>
        <TextField
          id={name}
          name={name}
          label={GeneralDetailsFields[name]}
          fullWidth
          disabled
          value={fields.masterPolicyReferenceNumber ?? ""}
        />
      </Grid>
    );
  }

  const renderMasterAssured = (name = "_masterAssuredOrMasterReassured") => {
    return (
      <Grid item>
        <TextField
          id={name}
          name={name}
          fullWidth
          disabled
          label={GeneralDetailsFields[name]}
          value={fields._masterAssuredOrMasterReassured ?? ""}
        />
      </Grid>
    );
  }

  const renderSecondLookupForMasterAssuredAndPolicyReference = (
    lookupType = "secondLookupForMasterAssuredAndMasterPolicyReference"
  ) => {
    return (<SecondMasterPolicyReferenceControl
      entities={["HDAC"]}
      fields={fields}
      control={control}
      errors={errors}
      lookupType={lookupType}
      forceClearDatasourceState={forceClearDatasourceState}
      disabled={!fields.underwriter || !fields.yoa || !fields.businessEntity}
      setBannerState={setBannerState}
      setLoading={setLoading}
      setFormState={setFormState}
      setValidationValue={setValidationValue}
      inputRef={ai?.fieldsRef?.[DefaultSecondMasterPolicyReferenceControlName]}
    />);
  }

  const renderSecondMasterPolicyReferenceNumber = (name = "secondMasterPolicyReferenceNumber") => {
    return (
      <Grid item>
        <TextField
          id={name}
          name={name}
          label={GeneralDetailsFields[name]}
          fullWidth
          disabled
          value={fields[name] ?? ""}
        />
      </Grid>
    );
  }
  const renderExpiringOrg = name => {
    return (
      <Grid item>
        <TextField
          id={name}
          name={name}
          label={GeneralDetailsFields[name]}
          fullWidth
          disabled
          value={fields[name] ?? ""}
        />
      </Grid>
    );
  }

  const renderSecondMasterAssured = (name = "_secondMasterAssuredOrMasterReassured") => {
    return (
      <Grid item>
        <TextField
          id={name}
          name={name}
          fullWidth
          disabled
          label={GeneralDetailsFields[name]}
          value={fields[name] ?? ""}
        />
      </Grid>
    );
  }

  const renderBrokerSection = () => {
    let render = fields.placingBasisCode !== PlacingBases.Declaration;

    return render && <>
      {renderBrokerCode()}
      {renderBrokerContact()}
    </>;
  }

  const renderIndividualFiltersSection = () => {
    let render = fields.typeOfReassured === TypesOfReassured.Individual;

    return render && <>
      {renderDateOfBirth("dateOfBirth")}
      {renderAddress("address")}
      {renderPostCode("postCode")}
    </>;
  }

  const renderRenewalIndividualFiltersSection = () => {
    let render = fields.renewalTypeOfReassured === TypesOfReassured.Individual;

    return render && <>
      {renderDateOfBirth("renewalDateOfBirth")}
      {renderAddress("renewalAddress")}
      {renderPostCode("renewalPostCode")}
    </>;
  }

  const renderMasterPolicySection = () => {
    if (fields.placingBasisCode !== PlacingBases.Declaration) {
      return false;
    }

    if (!isDualStampEntity(fields.businessEntity)) {
      return <>
        {renderLookupForMasterAssuredAndPolicyReference()}
        {renderMasterPolicyReferenceNumber()}
        {renderMasterAssured()}
      </>;
    }

    return <>
      {renderLookupForMasterAssuredAndPolicyReference("lookupForMasterAssuredAndMasterPolicyReference4000")}
      {renderMasterPolicyReferenceNumber("masterPolicyReferenceNumber4000")}
      {renderMasterAssured("_masterAssuredOrMasterReassured4000")}
      {renderSecondLookupForMasterAssuredAndPolicyReference()}
      {renderSecondMasterPolicyReferenceNumber()}
      {renderSecondMasterAssured()}
    </>;
  }

  const renderSubmissionPolicySection = () => {
    const render = !fields.renewedPolicy;
    return render && <>
      {renderSendTaskToOperations(false)}
      {renderHighPrioritySanctionsCheck()}
      {renderUnderwriter()}
      {renderYearOfAccount()}
      {renderEntity()}
      {renderPlacingBasis()}
      {renderBrokerSection()}
      {renderOperatingTerritory()}
      {renderDeclineImmediately()}
      {renderTypeOfReassured("typeOfReassured", onTypeOfReassuredChange)}
      {renderIndividualFiltersSection()}
      {renderCountry("countryName", "typeOfReassured", onCountryChange)}
      {renderAssured()}
      {renderMasterPolicySection()}
      {renderReassured()}
      {renderCountryOfIncorporation()}
      {renderCompanyForSanctionsScreening()}
      {renderAssuredEclipseLookup()}
      {renderCountry("assuredCountryName", "typeOfReassured", onAssuredCountryChange, false, true)}
      {renderCoverHolder()}
      {renderTriaSwitch()}
      {renderNumberBox("triaPremium", 6, false, true)}
      {renderTypeOfModellingRisk()}
      {renderInceptionDate()}
      {renderConditionCode()}
      {renderPricingNotRequired()}
      {renderSurplusLine()}
      {renderEeaRisk()}
      {renderNumberBox("rtbls", 3, true, false)}
      {renderSanctionWarning()}
    </>;
  }

  const renderRenewedPolicySection = () => {
    const render = fields.renewedPolicy;
    return render && <>
      {renderExpiringReferenceSwitch()}
      {renderExpiringPolicyReference()}
      {renderExpiringProgrammeReference()}
      {renderRenewalSection()}
      {renderTypeOfModellingRisk()}
      {renderEeaRisk()}
      {renderSanctionWarning()}
    </>;
  }

  return <>
    {renderRenewedPolicyCheckbox()}
    {renderRenewedPolicySection()}
    {renderSubmissionPolicySection()}
  </>;
}

export default SubmissionSection;
